import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { VALID_ANSWER_ENGINE_TABS } from "@/const";
import "bootstrap/dist/css/bootstrap.min.css";
import "./sidebar.css";

const Sidebar = ({ currentPage, isPermanentlyExpanded }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(isPermanentlyExpanded);

  // Update expanded state when isPermanentlyExpanded changes
  useEffect(() => {
    setExpanded(isPermanentlyExpanded);
  }, [isPermanentlyExpanded]);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get("tab");
  const isLegacySettings = requestedTab === "settings";
  const activeTab = isLegacySettings
    ? "sandbox"
    : VALID_ANSWER_ENGINE_TABS.has(requestedTab)
      ? requestedTab
      : "sandbox";

  const [aeSubmenuOpen, setAeSubmenuOpen] = useState(
    currentPage === "answer-engine"
  );

  useEffect(() => {
    if (currentPage === "answer-engine") {
      setAeSubmenuOpen(true);
    }
  }, [currentPage]);

  const handleMouseEnter = () => {
    if (!isPermanentlyExpanded) {
      setExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPermanentlyExpanded) {
      setExpanded(false);
    }
  };

  return (
    <div
      className={`sidebar ${expanded ? "expanded" : "collapsed"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="sidebar-header">
        <div className="logo-container">
          {expanded ? (
            <img
              src="https://cdn.geo.elelem.ai/logo.png"
              alt="Logo"
              className="logo-image"
            />
          ) : (
            <img
              src="https://cdn.geo.elelem.ai/logo_minimized.png"
              alt="Logo"
              className="logo-image"
            />
          )}
        </div>
      </div>

      <ul className="nav flex-column sidebar-nav">
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "geo-dashboard" ? "active" : ""
            }`}
            to="/geo-dashboard"
            data-testid="sidebar-geo-dashboard-link"
          >
            <i className="bi bi-bar-chart-line"></i>
            <span className="nav-text">
              {t("nav.geoDashboard", "GEO Dashboard")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "geo-comparison" ? "active" : ""
            }`}
            to="/geo-comparison"
            data-testid="sidebar-geo-comparison-link"
          >
            <i className="bi bi-arrow-left-right"></i>
            <span className="nav-text">
              {t("nav.geoComparison", "GEO Comparison")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "edit-query" ? "active" : ""
            }`}
            to="/edit-prompt"
            data-testid="sidebar-edit-query-link"
          >
            <i className="bi bi-collection"></i>
            <span className="nav-text">
              {t("nav.promptLibrary", "Prompt Library")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "query-research" ? "active" : ""
            }`}
            to="/prompt-research"
            data-testid="sidebar-query-research-link"
          >
            <i className="bi bi-search"></i>
            <span className="nav-text">
              {t("nav.promptResearch", "Prompt Research")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "optimize-content" ? "active" : ""
            }`}
            to="/optimize-content"
            data-testid="sidebar-optimize-content-link"
          >
            <i className="bi bi-globe"></i>
            <span className="nav-text">
              {t("nav.optimizeContent", "Optimize Content")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${currentPage === "tracking" ? "active" : ""}`}
            to="/tracking"
            data-testid="sidebar-tracking-link"
          >
            <i className="bi bi-activity"></i>
            <span className="nav-text">
              {t("nav.tracking", "Tracking")}
            </span>
          </Link>
        </li>
        <li className="nav-item">
          <Link
            className={`nav-link ${
              currentPage === "answer-engine" ? "active" : ""
            }`}
            to="/answer-engine"
            data-testid="sidebar-answer-engine-link"
            onClick={(e) => {
              if (currentPage === "answer-engine") {
                e.preventDefault();
                setAeSubmenuOpen(!aeSubmenuOpen);
              }
            }}
          >
            <i className="bi bi-people"></i>
            <span className="nav-text">
              {t("nav.answerEngine", "Answer Engine")}
            </span>
            {expanded && (
              <i
                className={`bi bi-chevron-down ms-auto submenu-chevron ${
                  aeSubmenuOpen ? "open" : ""
                }`}
                style={{ fontSize: "0.8rem", transition: "transform 0.2s" }}
              ></i>
            )}
          </Link>
          {expanded && aeSubmenuOpen && (
            <ul className="nav flex-column ps-4 ae-submenu" style={{ fontSize: "14px" }}>
              <li className="nav-item">
                <Link
                  className={`nav-link py-1 px-2 rounded-2 ${
                    activeTab === "sandbox" ? "active-sub" : ""
                  }`}
                  to="/answer-engine?tab=sandbox"
                  data-testid="sidebar-ae-sandbox-link"
                >
                  <i className="bi bi-chat-dots" style={{ fontSize: "1rem", marginRight: "10px" }}></i>
                  <span>{t("answerEngine.tabs.sandbox", "Sandbox")}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link py-1 px-2 rounded-2 ${activeTab === "knowledge-base" ? "active-sub" : ""}`}
                  to="/answer-engine?tab=knowledge-base"
                  data-testid="sidebar-ae-knowledge-base-link"
                >
                  <i className="bi bi-database" style={{ fontSize: "1rem", marginRight: "10px" }}></i>
                  <span>{t("answerEngine.tabs.knowledgeBase", "Knowledge Base")}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link py-1 px-2 rounded-2 ${activeTab === "ae-queries" ? "active-sub" : ""}`}
                  to="/answer-engine?tab=ae-queries"
                  data-testid="sidebar-ae-queries-link"
                >
                  <i className="bi bi-search" style={{ fontSize: "1rem", marginRight: "10px" }}></i>
                  <span>{t("answerEngine.tabs.aeQueries", "Live Queries")}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link py-1 px-2 rounded-2 ${activeTab === "chat-sessions" ? "active-sub" : ""}`}
                  to="/answer-engine?tab=chat-sessions"
                  data-testid="sidebar-ae-chat-sessions-link"
                >
                  <i className="bi bi-chat-left-text" style={{ fontSize: "1rem", marginRight: "10px" }}></i>
                  <span>{t("answerEngine.tabs.chatSessions", "Export Chats")}</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className={`nav-link py-1 px-2 rounded-2 ${activeTab === "analytics" ? "active-sub" : ""}`}
                  to="/answer-engine?tab=analytics"
                  data-testid="sidebar-ae-analytics-link"
                >
                  <i className="bi bi-bar-chart-line" style={{ fontSize: "1rem", marginRight: "10px" }}></i>
                  <span>{t("answerEngine.tabs.analytics", "Analytics")}</span>
                </Link>
              </li>
            </ul>
          )}
        </li>
      </ul>
    </div>
  );
};

export default Sidebar;
